import { Button, Container } from "@brightagency/headstart-common";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import React, { useMemo } from "react";
import { useAssessmentState } from "../../../services/assessment-state";
import { Screens } from "../../../services/assessment-state/types";
import { useSavedItems, useSavedItemsData } from "../../../utils/hooks";
import { Separator } from "../../atoms/Separator";
import { ListingCard } from "../../molecules/ListingCard";
import { PageCard } from "../../molecules/PageCard";
import { SavedAssessmentResults } from "../../molecules/SavedAssessmentResults";
import { useCardCarousel } from "../Carousel/controller";
import { CarouselSection } from "../CarouselSection";
import { Skeleton } from "./Skeleton";
import { Section } from "./styled";

export const SavedItemsContent = () => {
    const { clearAllItems } = useSavedItems();
    const { data, error, isLoading: loading } = useSavedItemsData();
    const { listings = [], pages = [] } = data ?? {};

    const hasListings = Boolean(listings.length);
    const hasPages = Boolean(pages.length);

    const { state } = useAssessmentState();

    const finishedAssessment = state.currentScreen === Screens.Results;

    const physicalListings = useMemo(() => listings?.filter((listing) => listing.acf.location_toggle !== 'digital'), [listings]);
    const digitalListings = useMemo(() => listings?.filter((listing) => listing.acf.location_toggle === 'digital'), [listings]);

    const hasPhysicalListings = Boolean(physicalListings?.length);
    const hasDigitalListings = Boolean(digitalListings?.length);

    const ListingPhysicalCarousel = useCardCarousel(physicalListings.length);
    const ListingDigitalCarousel = useCardCarousel(digitalListings.length);
    const PageCarousel = useCardCarousel(pages.length);

    if (error) {
        return (
            <Section>
                <Container>
                    <p>Something went wrong when fetching your saved items</p>;
                </Container>
            </Section>
        );
    }

    if (loading && !hasListings) {
        return <CarouselSection title="Loading..." />;
    } 

    return (
        <>
            {finishedAssessment && (
                <>
                    <Section>
                        <Container>
                            <SavedAssessmentResults />
                        </Container>
                    </Section>
                    <Separator />
                </>
            )}
            {!hasListings &&
                <CarouselSection title="Saved services">
                    <Skeleton />
                </CarouselSection>
            }
            {hasPhysicalListings &&
                <CarouselSection
                    title="Saved services"
                    carousel={
                        hasPhysicalListings
                            ? {
                                onScrollLeft: ListingPhysicalCarousel.scrollLeft,
                                onScrollRight: ListingPhysicalCarousel.scrollRight,
                                canScroll: ListingPhysicalCarousel.canScroll,
                                displayControl: true,
                                content: (
                                    <ListingPhysicalCarousel.Carousel>
                                        {physicalListings.map(listing => (
                                            <ListingCard key={listing.id} listing={listing} compact={false} />
                                        ))}
                                    </ListingPhysicalCarousel.Carousel>
                                ),
                            }
                            : undefined
                    }
                >
                    {!hasPhysicalListings && <Skeleton />}
                </CarouselSection>
            }
            { hasDigitalListings &&
                <CarouselSection
                    title="Saved phone and online services"
                    carousel={
                        hasDigitalListings
                            ? {
                                onScrollLeft: ListingDigitalCarousel.scrollLeft,
                                onScrollRight: ListingDigitalCarousel.scrollRight,
                                canScroll: ListingDigitalCarousel.canScroll,
                                displayControl: true,
                                content: (
                                    <ListingDigitalCarousel.Carousel>
                                        {digitalListings.map(listing => (
                                            <ListingCard key={listing.id} listing={listing} compact={false} />
                                        ))}
                                    </ListingDigitalCarousel.Carousel>
                                ),
                            }
                            : undefined
                    }
                >
                    {!hasDigitalListings && <Skeleton />}
                </CarouselSection>
            }
            {hasPages && (
                <>
                    <Separator />
                    <CarouselSection
                        title="Saved pages"
                        carousel={{
                            onScrollLeft: PageCarousel.scrollLeft,
                            onScrollRight: PageCarousel.scrollRight,
                            canScroll: PageCarousel.canScroll,
                            displayControl: true,
                            content: (
                                <PageCarousel.Carousel>
                                    {pages.map(page => (
                                        <PageCard key={page.id} page={page} />
                                    ))}
                                </PageCarousel.Carousel>
                            ),
                        }}
                    />
                </>
            )}
            {(hasPages || hasListings) && (
                <>
                    <Separator />
                    <Section>
                        <div style={{ textAlign: "center", marginBottom: 24 }}>
                            <Button
                                onClick={clearAllItems}
                                iconBefore={<DeleteRoundedIcon />}
                                appearance="secondary"
                                maxWidth={300}
                            >
                                Clear saved resources
                            </Button>
                        </div>
                    </Section>
                </>
            )}
        </>
    );
};
